/**
 * chartTheme — shared color palette + theme object for ECharts and
 * Recharts so every chart in the app speaks the same visual language.
 *
 * Resolution model: tokens live in tokens.css and are read at runtime
 * via getComputedStyle on <html>. Charts re-read on theme change.
 *
 * Usage:
 *   import { useChartTheme } from 'core/ui/chartTheme';
 *   const theme = useChartTheme();
 *   <ReactECharts option={...} theme={theme.echarts} />
 *   const colors = theme.palette;
 */

import { useEffect, useState } from 'react';
import { useTheme } from './theme';

const readVar = (name) => {
  if (typeof window === 'undefined') return '';
  return getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
};

/**
 * Six-hue, desaturated palette. Accent first, neighbors after, so a
 * single-series chart picks the brand color by default and multi-
 * series charts walk through coexisting hues without screaming.
 */
const PALETTE_DARK = [
  '#6e9bff', // accent
  '#5bc28e', // success
  '#e9b265', // warning
  '#ec6f6a', // danger
  '#a78bfa', // muted violet
  '#5dd3d3', // muted teal
];

const PALETTE_LIGHT = [
  '#1a59f0',
  '#14794a',
  '#a45c00',
  '#b3261e',
  '#6d4ddd',
  '#0e8a8a',
];

function buildTheme(mode) {
  const palette = mode === 'dark' ? PALETTE_DARK : PALETTE_LIGHT;
  const text = readVar('--text') || (mode === 'dark' ? '#e9e8e3' : '#1a1a1a');
  const textMuted =
    readVar('--text-muted') || (mode === 'dark' ? '#a4a39d' : '#5a5a5a');
  const border =
    readVar('--border') || (mode === 'dark' ? '#262a30' : '#e7e5df');
  const bgElevated =
    readVar('--bg-elevated') || (mode === 'dark' ? '#15171b' : '#ffffff');

  return {
    mode,
    palette,
    text,
    textMuted,
    border,
    bgElevated,

    /* Theme object passed to <ReactECharts theme={...}/>. */
    echarts: {
      color: palette,
      backgroundColor: 'transparent',
      textStyle: { color: text, fontFamily: 'Inter, system-ui, sans-serif' },
      title: { textStyle: { color: text } },
      legend: { textStyle: { color: textMuted } },
      tooltip: {
        backgroundColor: bgElevated,
        borderColor: border,
        textStyle: { color: text },
      },
      categoryAxis: {
        axisLine: { lineStyle: { color: border } },
        axisTick: { show: false },
        axisLabel: { color: textMuted, fontSize: 11 },
        splitLine: { show: false },
      },
      valueAxis: {
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { color: textMuted, fontSize: 11 },
        splitLine: { lineStyle: { color: border } },
      },
    },

    /* Defaults passed to Recharts pieces. */
    recharts: {
      grid:    { stroke: border, strokeDasharray: '3 3' },
      axis:    { stroke: border, fill: textMuted, fontSize: 11 },
      tooltip: {
        contentStyle: {
          background: bgElevated,
          border: `1px solid ${border}`,
          borderRadius: 6,
          color: text,
          fontSize: 12,
        },
        labelStyle: { color: text, fontWeight: 600 },
        itemStyle:  { color: text },
      },
    },
  };
}

/**
 * Hook: returns a chart theme object tied to the current data-theme
 * value on <html>. Rebuilds when the theme toggles.
 */
export function useChartTheme() {
  const { theme } = useTheme();
  const [chartTheme, setChartTheme] = useState(() => buildTheme(theme));

  useEffect(() => {
    setChartTheme(buildTheme(theme));
  }, [theme]);

  return chartTheme;
}

/**
 * Non-hook accessor — for places that can't call hooks (utility
 * functions, ECharts option factories called inside render). Reads
 * the current document theme.
 */
export function getChartTheme() {
  const mode =
    (typeof document !== 'undefined' &&
      document.documentElement.dataset.theme) ||
    'dark';
  return buildTheme(mode);
}
